import {
  Check,
  CheckCheck,
  MoveRight,
  Square,
  SquareStack,
} from 'lucide-react';

import type { TtqBehaviorId } from './ttqBehaviorCatalog';

export type TtqRelationNode = 'question-one' | 'question-many' | 'solution-one' | 'solution-many';

export type TtqAngleRelation = {
  left: TtqRelationNode;
  right: TtqRelationNode;
  caption: string;
};

const NODE_HINT: Record<TtqRelationNode, string> = {
  'question-one': '1题',
  'question-many': '多题',
  'solution-one': '1解',
  'solution-many': '多解',
};

export function getTtqAngleRelation(angleId: TtqBehaviorId): TtqAngleRelation | null {
  switch (angleId) {
    case 'multi_solution':
      return { left: 'question-one', right: 'solution-many', caption: '一题多解' };
    case 'question_chain':
      return { left: 'question-one', right: 'question-many', caption: '一题多题' };
    case 'pattern_unify':
      return { left: 'solution-one', right: 'question-many', caption: '一解多题' };
    case 'questions_chain':
      return { left: 'question-many', right: 'question-many', caption: '多题多题' };
    case 'cttq_converge':
      return { left: 'question-many', right: 'question-one', caption: '收敛品题' };
    case 'session_aggregate_a':
      return { left: 'question-many', right: 'solution-one', caption: '共同能力' };
    case 'session_aggregate_b':
      return { left: 'question-many', right: 'question-many', caption: '穿插变式' };
    default:
      return null;
  }
}

function iconSize(size: 'sm' | 'md' | 'lg'): number {
  if (size === 'sm') return 16;
  if (size === 'lg') return 22;
  return 18;
}

function RelationNode({
  node,
  size,
  muted,
}: {
  node: TtqRelationNode;
  size: 'sm' | 'md' | 'lg';
  muted?: boolean;
}) {
  const px = iconSize(size);
  const tone = muted ? 'text-muted' : 'text-ink';

  if (node === 'solution-one') {
    return (
      <span className={`inline-flex flex-col items-center gap-0.5 ${tone}`} title="一个解">
        <Check size={px} strokeWidth={2.25} aria-hidden />
        <span className="text-[9px] leading-none text-muted">{NODE_HINT[node]}</span>
      </span>
    );
  }

  if (node === 'solution-many') {
    return (
      <span className={`inline-flex flex-col items-center gap-0.5 ${tone}`} title="多个解">
        <CheckCheck size={px} strokeWidth={2.25} aria-hidden />
        <span className="text-[9px] leading-none text-muted">{NODE_HINT[node]}</span>
      </span>
    );
  }

  const QuestionIcon = node === 'question-many' ? SquareStack : Square;
  return (
    <span className={`inline-flex flex-col items-center gap-0.5 ${tone}`} title={node === 'question-many' ? '多道题' : '一道题'}>
      <QuestionIcon size={px} strokeWidth={2} aria-hidden />
      <span className="text-[9px] leading-none text-muted">{NODE_HINT[node]}</span>
    </span>
  );
}

type TtqAngleRelationIconProps = {
  angleId: TtqBehaviorId;
  size?: 'sm' | 'md' | 'lg';
  showCaption?: boolean;
  /** 覆盖默认 caption（如工作台标题「一题多解工作台」） */
  captionOverride?: string;
  muted?: boolean;
  className?: string;
};

export function TtqAngleRelationIcon({
  angleId,
  size = 'md',
  showCaption = true,
  captionOverride,
  muted = false,
  className = '',
}: TtqAngleRelationIconProps) {
  const relation = getTtqAngleRelation(angleId);
  if (!relation) return null;

  const caption = captionOverride ?? relation.caption;
  const arrowPx = iconSize(size);

  return (
    <div
      className={`inline-flex flex-col items-center gap-1 ${className}`}
      role="img"
      aria-label={`${caption}：${NODE_HINT[relation.left]}变${NODE_HINT[relation.right]}`}
    >
      <div className="inline-flex items-center gap-1.5">
        <RelationNode node={relation.left} size={size} muted={muted} />
        <MoveRight size={arrowPx} className={muted ? 'text-muted' : 'text-primary/70'} strokeWidth={2} aria-hidden />
        <RelationNode node={relation.right} size={size} muted={muted} />
      </div>
      {showCaption ? (
        <span className={`text-xs font-semibold ${muted ? 'text-muted' : 'text-ink'}`}>
          {caption}
        </span>
      ) : null}
    </div>
  );
}
